// Spoke Totals — stand-in for the Tableau sheet "Referral Breakdown by Spoke
// per Selected Hub" (workbook: Referral Report with Spoke Totals).
//
// A hub is the receiving facility. Each spoke is a sending facility that
// referred into that hub. Cells count referrals (Tableau's CNT(Guid) is one
// per referral; the cache stores one row per referral).
//
// Facility labels come from the current VitalEngine facility list, not the
// name frozen on the referral. A self-signup placeholder (VEF + that row's
// sending user id) is counted under the user's current facility when that
// facility has a real name.
//
// Scope limits which hubs appear. Spokes outside the scope still show,
// because they are the inbound sources.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCache } from '../../hooks/useCache';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import { useScopeSync } from '../../hooks/useScopeSync';
import { useCurrentScope } from '../../hooks/useCurrentScope';
import { useScope } from '../../contexts/ScopeContext';
import ScopeBar from '../../components/ScopeBar';
import FacilityFilter from '../../components/FacilityFilter';
import GroupFacilitiesModal from '../../components/GroupFacilitiesModal';
import { isTestRecord } from '../../utils/testDataFilter';
import { canonicalVeId } from '../../utils/drilldownAggregate';
import { recordDateYmd } from '../../utils/userAnalytics';
import { downloadObjectsAsXlsx, heatmapToObjects } from '../../utils/exportTableauSheet';
import './Drilldown20.css';
import './UserLoginsByClient.css';
import './SpokeTotals.css';

const f = (r, k) => (r?.data || r?.Data || {})[k];

const DATE_PRESETS = [
  { id: 'last_month', label: 'Last 30 days' },
  { id: 'last_3_months', label: 'Last 3 months' },
  { id: 'last_6_months', label: 'Last 6 months' },
  { id: 'ytd', label: 'Year to date' },
  { id: 'last_year', label: 'Last 12 months' },
  { id: 'custom', label: 'Custom range' },
];

const PAGE_SIZE = 200;

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function rangeFromPreset(preset) {
  const end = new Date();
  const start = new Date();
  switch (preset) {
    case 'last_month': start.setDate(start.getDate() - 30); break;
    case 'last_3_months': start.setMonth(start.getMonth() - 3); break;
    case 'last_6_months': start.setMonth(start.getMonth() - 6); break;
    case 'ytd': start.setMonth(0); start.setDate(1); break;
    case 'last_year': start.setFullYear(start.getFullYear() - 1); break;
    default: return { start: null, end: null };
  }
  return { start, end };
}

function toDate(ymdStr) {
  const iso = String(ymdStr || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!iso) return null;
  return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
}

function pad(n) {
  return String(n).padStart(2, '0');
}

function sundayStart(date) {
  const x = new Date(date.getTime());
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - x.getDay());
  return x;
}

function monthName(date) {
  return date.toLocaleString(undefined, { month: 'short' });
}

function columnForDate(date, granularity) {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const quarter = Math.floor((month - 1) / 3) + 1;
  const yearLabel = String(year);
  const monthLabel = `${monthName(date)} ${year}`;
  if (granularity === 'year') {
    return { key: yearLabel, yearLabel, quarterLabel: '', monthLabel: '', leafLabel: yearLabel };
  }
  if (granularity === 'quarter') {
    return { key: `${year}-Q${quarter}`, yearLabel, quarterLabel: `Q${quarter}`, monthLabel: '', leafLabel: `Q${quarter}` };
  }
  if (granularity === 'month') {
    return { key: `${year}-${pad(month)}`, yearLabel, quarterLabel: `Q${quarter}`, monthLabel, leafLabel: monthName(date) };
  }
  const start = sundayStart(date);
  return {
    key: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`,
    yearLabel: String(start.getFullYear()),
    quarterLabel: `Q${Math.floor(start.getMonth() / 3) + 1}`,
    monthLabel: `${monthName(start)} ${start.getFullYear()}`,
    leafLabel: String(start.getDate()),
  };
}

function enumerateColumns(startYmd, endYmd, granularity) {
  const start = toDate(startYmd);
  const end = toDate(endYmd);
  if (!start || !end || start > end) return [];
  const seen = new Map();
  const cursor = new Date(start.getTime());
  cursor.setHours(0, 0, 0, 0);
  while (cursor <= end) {
    const col = columnForDate(cursor, granularity);
    if (!seen.has(col.key)) seen.set(col.key, col);
    cursor.setDate(cursor.getDate() + 1);
  }
  const columns = [...seen.values()];
  return columns.map((col, i) => {
    if (i === 0) return { ...col, boundary: 'pane' };
    const prev = columns[i - 1];
    const quarterChanged = Boolean(col.quarterLabel) && col.quarterLabel !== prev.quarterLabel;
    if (col.yearLabel !== prev.yearLabel || quarterChanged) return { ...col, boundary: 'quarter' };
    if (col.monthLabel && col.monthLabel !== prev.monthLabel) return { ...col, boundary: 'month' };
    return { ...col, boundary: '' };
  });
}

function headerBands(columns, field) {
  const bands = [];
  for (const col of columns) {
    const label = col[field];
    if (!label) continue;
    const last = bands[bands.length - 1];
    if (last && last.label === label) last.span += 1;
    else bands.push({ label, span: 1, key: `${field}-${bands.length}-${label}` });
  }
  return bands;
}

const EMPTY_DIRECTORY = { facilities: new Map(), users: new Map() };

function directoryFromPayload(data) {
  const facilities = new Map();
  for (const row of data?.facilities || []) {
    const id = canonicalVeId(row.id ?? row.facilityId);
    const name = String(row.name || '').trim();
    if (id && name) facilities.set(id, name);
  }
  const users = new Map();
  for (const row of data?.users || []) {
    const userId = canonicalVeId(row.userId);
    const facilityId = canonicalVeId(row.facilityId);
    if (userId && facilityId) users.set(userId, facilityId);
  }
  return { facilities, users };
}

function isVefCode(name) {
  return /^VEF\d+$/i.test(String(name || '').trim());
}

/** True when the referral still carries the self-signup placeholder for this user. */
function isUserPlaceholder(name, userId) {
  const match = String(name || '').trim().match(/^VEF(\d+)$/i);
  return Boolean(match && userId && canonicalVeId(match[1]) === canonicalVeId(userId));
}

function facilityLabel(directory, facilityId, stampedName, fallback) {
  const current = facilityId ? directory.facilities.get(facilityId) : '';
  return current || stampedName || fallback;
}

function resolveSpoke(raw, directory) {
  const stampedId = canonicalVeId(f(raw, 'sending_facility_id'));
  const stampedName = f(raw, 'sending_facility') || '';
  const userId = canonicalVeId(f(raw, 'sending_user_id'));
  let spokeId = stampedId || stampedName || '—';
  let spokeName = facilityLabel(
    directory,
    stampedId,
    stampedName,
    stampedId ? `Facility ${stampedId}` : 'Unknown spoke',
  );

  if (isUserPlaceholder(stampedName, userId)) {
    const currentId = directory.users.get(userId);
    const currentName = currentId ? directory.facilities.get(currentId) : '';
    if (currentId && currentName && !isVefCode(currentName) && currentId !== spokeId) {
      spokeId = currentId;
      spokeName = currentName;
    }
  }

  return { spokeId, spokeName };
}

function typeMatches(raw, filter) {
  const n = Number(f(raw, 'referral_type'));
  if (filter === '0') return n === 0;
  if (filter === 'quick') return n === 3;
  if (filter === 'standard') return n !== 3;
  return true;
}

function aggregate({ referrals, granularity, startYmd, endYmd, directory = EMPTY_DIRECTORY }) {
  const columns = enumerateColumns(startYmd, endYmd, granularity);
  const colIndex = new Map(columns.map((c, i) => [c.key, i]));
  const hubs = new Map();

  for (const raw of referrals) {
    const day = recordDateYmd(raw);
    const date = toDate(day);
    if (!date) continue;
    const col = columnForDate(date, granularity);
    const ci = colIndex.get(col.key);
    if (ci == null) continue;

    const hubId = canonicalVeId(f(raw, 'receiving_facility_id')) || f(raw, 'receiving_facility') || '—';
    const hubName = facilityLabel(
      directory,
      canonicalVeId(f(raw, 'receiving_facility_id')),
      f(raw, 'receiving_facility') || '',
      hubId !== '—' ? `Facility ${hubId}` : 'Unknown hub',
    );
    const { spokeId, spokeName } = resolveSpoke(raw, directory);

    if (!hubs.has(hubId)) hubs.set(hubId, { hubId, hubName, spokes: new Map() });
    const hub = hubs.get(hubId);
    if (!hub.spokes.has(spokeId)) {
      hub.spokes.set(spokeId, {
        rowKey: `${hubId}::${spokeId}`,
        hubKey: hubId,
        hubName,
        spokeName,
        isTotal: false,
        buckets: new Array(columns.length).fill(0),
        total: 0,
      });
    }
    const spoke = hub.spokes.get(spokeId);
    spoke.buckets[ci] += 1;
    spoke.total += 1;
  }

  const rows = [];
  const hubList = [...hubs.values()].sort((a, b) => a.hubName.localeCompare(b.hubName));
  for (const hub of hubList) {
    const spokes = [...hub.spokes.values()].sort((a, b) => b.total - a.total || a.spokeName.localeCompare(b.spokeName));
    const totalBuckets = new Array(columns.length).fill(0);
    let grand = 0;
    for (const spoke of spokes) {
      spoke.buckets.forEach((n, i) => { totalBuckets[i] += n; });
      grand += spoke.total;
      rows.push(spoke);
    }
    rows.push({
      rowKey: `${hub.hubId}::total`,
      hubKey: hub.hubId,
      hubName: hub.hubName,
      spokeName: 'Total',
      isTotal: true,
      buckets: totalBuckets,
      total: grand,
    });
  }
  return { columns, rows };
}

function spanSizes(rows, keyFn) {
  const sizes = new Array(rows.length).fill(0);
  let i = 0;
  while (i < rows.length) {
    const key = keyFn(rows[i]);
    let j = i + 1;
    while (j < rows.length && keyFn(rows[j]) === key) j += 1;
    sizes[i] = j - i;
    i = j;
  }
  return sizes;
}

function boundaryClass(boundary) {
  return boundary ? `ulc-group ulc-group-${boundary}` : '';
}

function HeatCell({ value, rowMax, boundary, emphasize }) {
  const cls = ['dd-cell', boundaryClass(boundary), value ? '' : 'dd-cell-empty', emphasize ? 'spoke-total-cell' : ''].filter(Boolean).join(' ');
  if (!value) return <td className={cls}>·</td>;
  const pct = rowMax > 0 ? Math.min(1, value / rowMax) : 0;
  const alpha = 0.15 + pct * 0.75;
  return (
    <td
      className={cls}
      style={emphasize ? undefined : {
        backgroundColor: `rgba(15, 118, 110, ${alpha.toFixed(3)})`,
        color: pct > 0.55 ? '#f8fafc' : '#134e4a',
      }}
    >
      {value.toLocaleString()}
    </td>
  );
}

function SpokeTotals() {
  const cache = useCache();
  const { customers, resolveCustomerFacilityIds } = useCustomerFilter();
  const { clearScope: clearGlobalScope } = useScope();

  const [granularity, setGranularity] = useState('month');
  const [dateRangePreset, setDateRangePreset] = useState('last_3_months');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [removeTestData, setRemoveTestData] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [hubFilter, setHubFilter] = useState('');
  const [nameQuery, setNameQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const [selectedFacilityIds, setSelectedFacilityIds] = useState(() => new Set());
  const [activeCustomerId, setActiveCustomerId] = useState('');
  const [activeFilterId, setActiveFilterId] = useState('');
  const [savedFilters, setSavedFilters] = useState([]);
  const [showFacilityFilter, setShowFacilityFilter] = useState(false);
  const [showScopeFacilities, setShowScopeFacilities] = useState(false);

  const [hierarchy, setHierarchy] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [directory, setDirectory] = useState(EMPTY_DIRECTORY);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await cache.getCachedFacilityHierarchy();
        if (r?.success) setHierarchy(r.hierarchy || []);
      } catch { /* optional */ }
      try {
        const r = await cache.getSavedFilters();
        if (r?.filters) setSavedFilters(r.filters);
      } catch { /* no filters yet */ }
    })();
    const { start, end } = rangeFromPreset('last_3_months');
    setStartDate(ymd(start));
    setEndDate(ymd(end));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dateRangePreset !== 'custom') {
      const { start, end } = rangeFromPreset(dateRangePreset);
      if (start && end) {
        setStartDate(ymd(start));
        setEndDate(ymd(end));
      }
    }
  }, [dateRangePreset]);

  const load = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError('');
    setHasLoaded(false);
    setVisibleCount(PAGE_SIZE);
    try {
      setProgress({ current: 0, total: 1, label: 'Referrals' });
      const [res, dirRes] = await Promise.all([
        cache.getCachedData('referrals', startDate, endDate),
        cache.getVeFacilityDirectory().catch(() => null),
      ]);
      setReferrals(res?.success ? (res.records || []) : []);
      setDirectory(dirRes?.success ? directoryFromPayload(dirRes) : EMPTY_DIRECTORY);
      setProgress({ current: 1, total: 1, label: 'Done' });
      setHasLoaded(true);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Load failed.');
    } finally {
      setLoading(false);
      setTimeout(() => setProgress(null), 1200);
    }
  }, [cache, startDate, endDate]);

  const { setScopeFromCustomer, setScopeFromFilter } = useScopeSync({
    customers,
    savedFilters,
    resolveCustomerFacilityIds,
    setSelectedFacilityIds,
    setActiveCustomerId,
    setActiveFilterId,
    loadData: load,
    ready: Boolean(startDate && endDate),
  });

  const currentScope = useCurrentScope({
    activeCustomerId,
    activeFilterId,
    selectedFacilityIds,
    customers,
    savedFilters,
    setActiveCustomerId,
    setActiveFilterId,
    setSelectedFacilityIds,
    clearGlobalScope,
  });

  const applySavedFilter = (filterId) => {
    setActiveFilterId(filterId);
    setActiveCustomerId('');
    if (!filterId) setSelectedFacilityIds(new Set());
    else {
      const sf = savedFilters.find(s => String(s.id) === String(filterId));
      if (sf?.facilityIds) setSelectedFacilityIds(new Set(sf.facilityIds.map(String)));
    }
    setScopeFromFilter(filterId);
  };

  const applyCustomerFilter = async (customerId) => {
    setActiveCustomerId(customerId);
    setActiveFilterId('');
    if (!customerId) setSelectedFacilityIds(new Set());
    else {
      const ids = await resolveCustomerFacilityIds(customerId);
      setSelectedFacilityIds(new Set(ids.map(String)));
    }
    setScopeFromCustomer(customerId);
  };

  const dataFacilityIds = useMemo(() => {
    const ids = new Set();
    for (const r of referrals) {
      const recv = canonicalVeId(f(r, 'receiving_facility_id'));
      const send = canonicalVeId(f(r, 'sending_facility_id'));
      if (recv) ids.add(recv);
      if (send) ids.add(send);
    }
    return ids;
  }, [referrals]);

  const filteredReferrals = useMemo(() => {
    let rows = referrals;
    if (removeTestData) rows = rows.filter(r => !isTestRecord(r));
    rows = rows.filter(r => typeMatches(r, typeFilter));
    if (selectedFacilityIds.size > 0) {
      const facIds = new Set([...selectedFacilityIds].map(canonicalVeId));
      rows = rows.filter(r => {
        const recv = canonicalVeId(f(r, 'receiving_facility_id'));
        return recv && facIds.has(recv);
      });
    }
    return rows;
  }, [referrals, removeTestData, selectedFacilityIds, typeFilter]);

  const result = useMemo(() => {
    if (!hasLoaded) return { columns: [], rows: [] };
    return aggregate({
      referrals: filteredReferrals,
      granularity,
      startYmd: startDate,
      endYmd: endDate,
      directory,
    });
  }, [hasLoaded, filteredReferrals, granularity, startDate, endDate, directory]);

  const hubs = useMemo(() => {
    const seen = new Map();
    for (const row of result.rows) {
      if (!seen.has(row.hubKey)) seen.set(row.hubKey, row.hubName);
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [result.rows]);

  const searchedRows = useMemo(() => {
    const q = nameQuery.trim().toLowerCase();
    let rows = result.rows;
    if (hubFilter) rows = rows.filter(r => r.hubKey === hubFilter);
    if (!q) return rows;
    const hubKeys = new Set();
    for (const r of rows) {
      const spokeHit = !r.isTotal && r.spokeName.toLowerCase().includes(q);
      if (r.hubName.toLowerCase().includes(q) || spokeHit) hubKeys.add(r.hubKey);
    }
    return rows.filter(r => hubKeys.has(r.hubKey));
  }, [result.rows, hubFilter, nameQuery]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [nameQuery, granularity, typeFilter, hubFilter, hasLoaded]);

  const visibleRows = searchedRows.slice(0, visibleCount);
  const hubSpans = spanSizes(visibleRows, r => r.hubKey);
  const showYear = granularity !== 'year';
  const showQuarter = granularity === 'week' || granularity === 'month';
  const showMonth = granularity === 'week';
  const yearBands = showYear ? headerBands(result.columns, 'yearLabel') : [];
  const quarterBands = showQuarter ? headerBands(result.columns, 'quarterLabel') : [];
  const monthBands = showMonth ? headerBands(result.columns, 'monthLabel') : [];
  const idRowSpan = 1 + Number(showYear) + Number(showQuarter) + Number(showMonth);

  return (
    <div className="dd20 ulc spoke">
      <p className="dd20-intro">
        Referrals into each hub, broken out by the sending facility (the spoke),
        in the spirit of the Tableau sheet
        <em> Referral Breakdown by Spoke per Selected Hub</em>.
        A customer or facility scope chooses the hubs. Spokes can sit outside that scope.
      </p>

      <div className="dd20-controls">
        <div className="dd20-field">
          <label htmlFor="spoke-hub">Hub</label>
          <select id="spoke-hub" value={hubFilter} onChange={e => setHubFilter(e.target.value)} disabled={!hasLoaded}>
            <option value="">All hubs in scope</option>
            {hubs.map(h => (
              <option key={h.id} value={h.id}>{h.name}</option>
            ))}
          </select>
        </div>

        <div className="dd20-field">
          <label htmlFor="spoke-type">Referral type</label>
          <select id="spoke-type" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="all">All types</option>
            <option value="0">Type 0 only</option>
            <option value="standard">Standard (not Quick)</option>
            <option value="quick">Quick referrals</option>
          </select>
        </div>

        <div className="dd20-field">
          <label htmlFor="spoke-period">Period</label>
          <select id="spoke-period" value={granularity} onChange={e => setGranularity(e.target.value)}>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
            <option value="quarter">Quarterly</option>
            <option value="year">Yearly</option>
          </select>
        </div>

        <div className="dd20-field">
          <label htmlFor="spoke-date">Date range</label>
          <select id="spoke-date" value={dateRangePreset} onChange={e => setDateRangePreset(e.target.value)} disabled={loading}>
            {DATE_PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>

        {dateRangePreset === 'custom' && (
          <>
            <div className="dd20-field">
              <label htmlFor="spoke-start">Start</label>
              <input id="spoke-start" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} disabled={loading} />
            </div>
            <div className="dd20-field">
              <label htmlFor="spoke-end">End</label>
              <input id="spoke-end" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} disabled={loading} />
            </div>
          </>
        )}

        <div className="dd20-field">
          <label htmlFor="spoke-search">Find</label>
          <input id="spoke-search" type="search" placeholder="Hub or spoke…" value={nameQuery} onChange={e => setNameQuery(e.target.value)} />
        </div>

        {savedFilters.length > 0 && (
          <div className="dd20-field">
            <label htmlFor="spoke-saved-filter">Saved filter</label>
            <select id="spoke-saved-filter" value={activeFilterId} onChange={e => applySavedFilter(e.target.value)} disabled={loading}>
              <option value="">None (show all)</option>
              {savedFilters.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.facilityIds?.length || 0})</option>
              ))}
            </select>
          </div>
        )}

        {customers.length > 0 && (
          <div className="dd20-field">
            <label htmlFor="spoke-customer">Customer</label>
            <select id="spoke-customer" value={activeCustomerId} onChange={e => applyCustomerFilter(e.target.value)} disabled={loading}>
              <option value="">None (show all)</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c._depth > 0 ? '— ' : ''}{c.name}</option>
              ))}
            </select>
          </div>
        )}

        <button type="button" className="dd20-filter-btn" onClick={() => setShowFacilityFilter(true)} disabled={loading}>
          Custom filter{selectedFacilityIds.size > 0 ? ` (${selectedFacilityIds.size})` : ''}
        </button>

        <label className="dd20-check">
          <input type="checkbox" checked={removeTestData} onChange={e => setRemoveTestData(e.target.checked)} />
          Remove test data
        </label>

        <button type="button" className="dd20-load" onClick={load} disabled={loading || !startDate || !endDate}>
          {loading ? 'Loading…' : 'Load data'}
        </button>
      </div>

      <ScopeBar
        scope={currentScope}
        onViewFacilities={
          currentScope.kind !== 'none' && hierarchy?.length && selectedFacilityIds.size
            ? () => setShowScopeFacilities(true)
            : null
        }
      />

      {showScopeFacilities && currentScope.kind !== 'none' && (
        <GroupFacilitiesModal
          groupName={currentScope.name}
          hierarchy={hierarchy}
          facilityIds={Array.from(selectedFacilityIds)}
          onClose={() => setShowScopeFacilities(false)}
        />
      )}

      {progress && <div className="dd20-progress">{progress.label} ({progress.current}/{progress.total})</div>}
      {error && <div className="dd20-error">{error}</div>}
      {!hasLoaded && !loading && (
        <div className="dd20-empty-first">
          Pick a customer or facility set to choose the hubs, then click <strong>Load data</strong>.
        </div>
      )}

      {hasLoaded && (
        <>
          <div className="dd20-section-header">
            <div>
              <h3>Referral breakdown by spoke per hub</h3>
              <p className="dd20-view-hint">
                {searchedRows.filter(r => !r.isTotal).length.toLocaleString()} spokes
                {' '}· {filteredReferrals.length.toLocaleString()} referrals
              </p>
            </div>
            <div className="dd20-section-actions">
              <button
                type="button"
                className="dd20-export-btn"
                disabled={!searchedRows.length}
                title="Download all filtered rows as Excel (includes hub Total rows)"
                onClick={() => {
                  const { rows: exportRows, headers } = heatmapToObjects(
                    { columns: result.columns, rows: searchedRows },
                    {
                      idColumns: [
                        { key: 'hubName', header: 'Hub' },
                        { key: 'spokeName', header: 'Spoke' },
                      ],
                    }
                  );
                  downloadObjectsAsXlsx(exportRows, {
                    fileStem: 'spoke_totals',
                    sheetName: 'Spoke Totals',
                    headers,
                    startDate,
                    endDate,
                  });
                }}
              >
                Export Excel
              </button>
            </div>
          </div>

          {visibleRows.length === 0 ? (
            <div className="dd-empty">No referrals match this window.</div>
          ) : (
            <div className="dd-table-scroll">
              <table className="dd-heatmap ulc-table spoke-table">
                <thead>
                  <tr>
                    <th className="ulc-id ulc-lock ulc-lock-1" rowSpan={idRowSpan}>Hub</th>
                    <th className="ulc-id ulc-lock ulc-lock-2" rowSpan={idRowSpan}>Spoke</th>
                    {showYear
                      ? yearBands.map(b => <th key={b.key} className="ulc-band" colSpan={b.span}>{b.label}</th>)
                      : result.columns.map(c => (
                        <th key={c.key} className={`dd-col-header ${boundaryClass(c.boundary)}`}>{c.leafLabel}</th>
                      ))}
                    <th className="dd-total-header ulc-pane" rowSpan={idRowSpan}>Grand total</th>
                  </tr>
                  {showQuarter && (
                    <tr>
                      {quarterBands.map(b => <th key={b.key} className="ulc-band ulc-band-sub" colSpan={b.span}>{b.label}</th>)}
                    </tr>
                  )}
                  {showMonth && (
                    <tr>
                      {monthBands.map(b => <th key={b.key} className="ulc-band ulc-band-sub" colSpan={b.span}>{b.label}</th>)}
                    </tr>
                  )}
                  {showYear && (
                    <tr>
                      {result.columns.map(c => (
                        <th key={c.key} className={`dd-col-header ${boundaryClass(c.boundary)}`}>{c.leafLabel}</th>
                      ))}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {visibleRows.map((r, idx) => {
                    const rowMax = Math.max(0, ...r.buckets);
                    const hubSpan = hubSpans[idx];
                    return (
                      <tr key={r.rowKey} className={r.isTotal ? 'dd-row spoke-total-row' : 'dd-row'}>
                        {hubSpan > 0 && (
                          <td className="ulc-id ulc-merged ulc-lock ulc-lock-1" rowSpan={hubSpan} title={r.hubName}>
                            {r.hubName}
                          </td>
                        )}
                        <td className={`ulc-id ulc-lock ulc-lock-2 ${r.isTotal ? 'spoke-total-label' : ''}`} title={r.spokeName}>
                          {r.spokeName}
                        </td>
                        {r.buckets.map((n, i) => (
                          <HeatCell
                            key={result.columns[i].key}
                            value={n}
                            rowMax={rowMax}
                            boundary={result.columns[i].boundary}
                            emphasize={r.isTotal}
                          />
                        ))}
                        <td className={`dd-row-total ulc-pane ${r.isTotal ? 'spoke-total-cell' : ''}`}>{r.total.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {searchedRows.length > visibleRows.length && (
            <button type="button" className="dd20-filter-btn ulc-more" onClick={() => setVisibleCount(n => n + PAGE_SIZE)}>
              Show more ({visibleRows.length.toLocaleString()} of {searchedRows.length.toLocaleString()})
            </button>
          )}
        </>
      )}

      <details className="dd20-gaps">
        <summary>How this maps to the Tableau sheet</summary>
        <ul>
          <li><strong>Hub</strong> is the receiving facility. <strong>Spoke</strong> is the sending facility.</li>
          <li><strong>Counts</strong> are referral rows. Tableau uses CNT(Guid); the cache does not store Guid separately, and each row is one referral.</li>
          <li>The published sheet filters <strong>Referral Type = 0</strong>. Use the type dropdown when you want that cut. All types is the default so a scope is not empty by surprise.</li>
        </ul>
      </details>

      {showFacilityFilter && (
        <FacilityFilter
          hierarchy={hierarchy}
          availableFacilityIds={dataFacilityIds}
          selectedFacilityIds={selectedFacilityIds}
          onSelectionChange={(ids) => {
            setSelectedFacilityIds(ids);
            setActiveCustomerId('');
            setActiveFilterId('');
            clearGlobalScope();
          }}
          onClose={() => setShowFacilityFilter(false)}
        />
      )}
    </div>
  );
}

export default SpokeTotals;
